import ModalUpdateConfiguration from '@components/modal-update-configuration/modal-update-configuration.component'
import { withTransaction } from '@elastic/apm-rum-react'
import {
  Button,
  DataHelper,
  ImpersonateInfo,
  Input,
  InputBirthday,
  InputCountries,
  InputDocumentCnpj,
  InputDocumentCpf,
  InputDocumentInternational,
  InputDocumentRut,
  InputGenders,
  InputName,
  InputPhone,
  InputRaces,
  LogService,
  MySquid,
  ProfileAdditionalInfo,
  TioPatinhasService,
  User,
  UserHelper,
  localForageHelper,
} from '@hub/shared'
import PhoneValidationComponent from '@src/pages/profile/components/phone-validation/phone-validation.component'
import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { SqCallout, SqLoader, SqModal } from '@squidit/react-css'
import './data.page.scoped.scss'

interface Props {
  user: User
  getInfos?: (infos?: ProfileAdditionalInfo) => void
  getValidation?: (valid: boolean) => void
  pushFormSubmit?: () => void
  submitConfirmation?: () => void
  registerCompleteness?: boolean
  openModalDocumentInputConfirmation: boolean
  phoneIsValid?: boolean
  setOpenModalDocumentInputConfirmation: (open: boolean) => void
  setHasConfirmDocumentInput: (confirmation: boolean) => void
  timeOuted: boolean
  setTimeOuted: (timeOuted: boolean) => void
  hasChanged?: (hasChanged?: boolean, message?: string) => void
  configs?: MySquid
  impersonateUser?: ImpersonateInfo | null
}

const DataPage = ({
  user,
  getInfos,
  getValidation,
  pushFormSubmit,
  submitConfirmation,
  registerCompleteness,
  openModalDocumentInputConfirmation,
  setOpenModalDocumentInputConfirmation,
  setHasConfirmDocumentInput,
  phoneIsValid,
  timeOuted,
  setTimeOuted,
  hasChanged,
  configs,
  impersonateUser = null,
}: Props) => {
  const logService = useMemo(() => new LogService(), [])
  const userHelper = useMemo(() => new UserHelper(), [])
  const dataHelper = useMemo(() => new DataHelper(), [])
  const tioService = useMemo(() => new TioPatinhasService(), [])
  const date = useMemo(() => new Date('08/19/2022'), [])

  logService.init('Data Page')

  const FIELDS_REQUIRED = ['name', 'email', 'nationality', 'document', 'birthday', 'race', 'gender', 'phone']

  const { t } = useTranslation('profilePage')
  const [allValid, setAllValid] = useState(false)
  const [initialRegister, setInitialRegister] = useState<ProfileAdditionalInfo>(new ProfileAdditionalInfo())
  const [register, setRegister] = useState<ProfileAdditionalInfo>(new ProfileAdditionalInfo())
  const [registerValidation, setRegisterValidation] = useState<ProfileAdditionalInfo>(
    register?.toValidation ? register?.toValidation(FIELDS_REQUIRED) : null
  )
  const [disabledDocument, setDisabledDocument] = useState(true)
  const [openOverlayPhoneValidation, setOpenOverlayPhoneValidation] = useState(false)
  const [loading, setLoading] = useState<boolean>(true)
  const [showModalUpdate, setShowModalUpdate] = useState<boolean>(false)

  useEffect(() => {
    tioService
      .canEditProfile()
      .then(async (canEdit) => {
        setDisabledDocument(!canEdit.canEditNationalDocument && !!register.document)
        const getShowModalUpdate = await localForageHelper.getItem('showModalUpdate')
        if (user?.identities?.length && Date.parse(user?.profileadditionalinfo?.createdAt || '') < date.getTime() && !getShowModalUpdate) {
          await localForageHelper.setItem('showModalUpdate', 'true')
          setShowModalUpdate(true)
        }
      })
      .catch((error) => {
        setDisabledDocument(true)
        logService.error({
          error,
          message: 'Error on init data page',
        })
      })
      .finally(() => setLoading(false))
  }, [
    userHelper,
    dataHelper,
    tioService,
    date,
    logService,
    register.document,
    user?.identities?.length,
    user?.profileadditionalinfo?.createdAt,
  ])

  useEffect(() => {
    const instanceOfRegister: ProfileAdditionalInfo = new ProfileAdditionalInfo(user?.profileadditionalinfo)
    // A linha abaixo é pelo fato de o back devolver a data timestamp (caso mude pode ser removida).
    instanceOfRegister.birthday = instanceOfRegister?.birthday?.split('T')[0] || instanceOfRegister?.birthday
    setRegister(instanceOfRegister)
    setInitialRegister(instanceOfRegister)
    if (instanceOfRegister?.toValidation) {
      setRegisterValidation({ ...registerValidation, ...instanceOfRegister.toValidation(FIELDS_REQUIRED) })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  const validate = useCallback(
    (fieldName: string, fieldValue: boolean) => {
      const newValidationObject = { ...registerValidation, [fieldName]: fieldValue }
      setRegisterValidation(newValidationObject)
      const newAllValid = Object.values(newValidationObject).every((value) => value === true)
      setAllValid(newAllValid)

      if (typeof getValidation === 'function') {
        getValidation(newAllValid)
      }
    },
    [registerValidation, getValidation]
  )

  const getDocumentType = useCallback((nationality: string): string => {
    let documentType = ''
    if (nationality === 'BR') {
      documentType = 'CPF'
    } else if (nationality === 'CL') {
      documentType = 'RUT'
    } else {
      documentType = 'International'
    }
    return documentType
  }, [])

  const onTimeout = (timeOuted: boolean) => {
    setTimeOuted(timeOuted)
  }

  const handleChange = (fieldName: string, fieldValue: string) => {
    let newRegister: ProfileAdditionalInfo | any = new ProfileAdditionalInfo({ ...register, [fieldName]: fieldValue })

    if (fieldName === 'nationality') {
      newRegister = new ProfileAdditionalInfo({
        ...register,
        documentType: getDocumentType(fieldValue),
        [fieldName]: fieldValue,
      })
    }
    if (fieldName === 'phone') {
      newRegister = new ProfileAdditionalInfo({
        ...register,
        phoneValid: user?.profileadditionalinfo?.phone !== fieldValue ? 0 : user?.profileadditionalinfo?.phoneValid,
        [fieldName]: fieldValue,
      })
    }
    if (fieldName === 'document') {
      newRegister = new ProfileAdditionalInfo({
        ...register,
        [fieldName]: fieldValue.replace(/[^\dkK]/g, ''),
      })
    }
    if (fieldName === 'socialName') {
      let isValid = isValidSocialName(fieldValue)
      validate(fieldName, isValid)
    }
    setRegister(newRegister)
    if (typeof getInfos === 'function') {
      getInfos(newRegister)
    }
    if (typeof hasChanged === 'function') {
      hasChanged(
        newRegister[fieldName as keyof ProfileAdditionalInfo] !== initialRegister[fieldName as keyof ProfileAdditionalInfo],
        t('unsavedDataMessage')
      )
    }
  }

  const isValidSocialName = useCallback((socialName: string) => {
    const hasSocialName = socialName !== ''
    return !hasSocialName || (/^[a-zA-ZÀ-ÿ\s]+$/g.test(socialName) && hasSocialName)
  }, [])

  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (allValid && typeof pushFormSubmit === 'function' && (!impersonateUser || impersonateUser?.impersonateAdmin)) {
      pushFormSubmit()
    }
  }

  const onConfirmModalDocument = (e: FormEvent) => {
    if (submitConfirmation && typeof submitConfirmation === 'function') {
      e.preventDefault()
      submitConfirmation()
    }
    setOpenModalDocumentInputConfirmation(false)
  }

  const documentInputModalConfirmation = () => {
    const footer = (
      <div className="actions col-24 display-flex justify-content-space-between">
        <Button
          color="transparent"
          textColor="var(--text_color)"
          borderColor="var(--gray_light)"
          onClick={() => setOpenModalDocumentInputConfirmation(false)}
        >
          {t('cancel')}
        </Button>
        <Button onClick={onConfirmModalDocument}>{t('confirm')}</Button>
      </div>
    )

    return (
      <SqModal
        className="change-document-modal"
        open={openModalDocumentInputConfirmation}
        onCloseChange={() => setOpenModalDocumentInputConfirmation(false)}
        header={<h5>{t('confirmDeletion')}</h5>}
        footer={footer}
      >
        <div style={{ backgroundColor: 'var(--background)', padding: '1rem', marginTop: '0.5rem', marginBottom: '0.5rem' }}>
          <h5>{t('title')}</h5>
          <p dangerouslySetInnerHTML={{ __html: t('subtitle') }} />
          <p className="pt-4" dangerouslySetInnerHTML={{ __html: t('description') }} />
        </div>
      </SqModal>
    )
  }

  if (loading) {
    return (
      <div className="data-page loader-wrapper">
        <SqLoader size="big" />
      </div>
    )
  }

  return (
    <div className="data-page">
      {showModalUpdate ? <ModalUpdateConfiguration onClose={() => setShowModalUpdate(false)} /> : null}
      <span>
        {!registerCompleteness ? (
          <SqCallout className="mt-0 mb-5" title={t('warning.title')}>
            <span dangerouslySetInnerHTML={{ __html: t('warning.message') }}></span>
          </SqCallout>
        ) : null}
        <form onSubmit={(e) => submit(e)} style={{ width: '100%' }}>
          <InputName
            label={t('fullName')}
            value={register?.name}
            className="mb-3"
            placeholder="Ex: John Doe"
            name="register-name"
            id="register-name"
            onChange={(value) => handleChange('name', value)}
            onValidate={(valid) => validate('name', valid)}
            requiredOnLoad
            readOnly={loading}
            autoComplete="name"
            required
          />
          <Input
            label={t('email')}
            type="email"
            placeholder="email@email.com"
            value={register?.email}
            className="mb-3"
            name="register-email"
            id="register-email"
            onChange={(value) => handleChange('email', value)}
            onValidate={(valid) => validate('email', valid)}
            requiredOnLoad
            autoComplete="email"
            required
          />
          <Input
            label={t('socialName')}
            type="text"
            placeholder=""
            value={register?.socialName}
            className="mb-3"
            name="register-social-name"
            id="register-social-name"
            onChange={(value) => handleChange('socialName', value)}
            externalError={!isValidSocialName(register?.socialName ?? '') ? t('socialNameError') : ''}
          />
          <InputCountries
            label={t('nationality')}
            placeholder={t('nationalityPlaceholder')}
            value={register?.nationality}
            className="mb-3"
            name="register-nationality"
            id="register-nationality"
            onChange={(value) => handleChange('nationality', value)}
            onValidate={(valid) => validate('nationality', valid)}
            requiredOnLoad
            disabled={loading || disabledDocument || register?.documentType === 'CNPJ'}
            required
          />
          {register?.documentType === 'CPF' && (
            <InputDocumentCpf
              className="mb-3"
              name="data-document"
              id="data-document"
              value={register?.document}
              label={'CPF'}
              onChange={(value) => handleChange('document', value)}
              onValidate={(valid) => validate('document', valid)}
              requiredOnLoad
              readOnly={loading}
              disabled={loading || disabledDocument}
              originalValue={user?.profileadditionalinfo?.document}
              required
            />
          )}
          {register?.documentType === 'RUT' && (
            <InputDocumentRut
              className="mb-3"
              name="data-document"
              id="data-document"
              value={register?.document}
              label={'RUT'}
              onChange={(value) => handleChange('document', value)}
              onValidate={(valid) => validate('document', valid)}
              requiredOnLoad
              readOnly={loading}
              disabled={loading || disabledDocument}
              originalValue={user?.profileadditionalinfo?.document}
              required
            />
          )}
          {register?.documentType === 'International' && (
            <InputDocumentInternational
              className="mb-3"
              name="data-document"
              id="data-document"
              value={register?.document}
              label={t('document')}
              nationality={register?.nationality}
              onChange={(value) => handleChange('document', value)}
              onValidate={(valid) => validate('document', valid)}
              requiredOnLoad
              readOnly={loading}
              disabled={loading || disabledDocument}
              originalValue={user?.profileadditionalinfo?.document}
              required
              onTimeout={onTimeout}
              loading={!timeOuted}
            />
          )}
          {register?.documentType === 'CNPJ' && (
            <InputDocumentCnpj
              className="mb-3"
              name="data-document"
              id="data-document"
              value={register?.document}
              label={'CNPJ'}
              onChange={(value) => handleChange('document', value)}
              onValidate={(valid) => validate('document', valid)}
              requiredOnLoad
              readOnly={loading}
              disabled={loading || disabledDocument}
              required
            />
          )}
          <InputBirthday
            label={t('birthdate')}
            value={register?.birthday}
            name="register-birthdate"
            id="register-birthdate"
            className="mb-3"
            type="date"
            placeholder={'dd/mm/yyyy'}
            onChange={(value) => handleChange('birthday', value)}
            onValidate={(valid) => validate('birthday', valid)}
            requiredOnLoad
            readOnly={loading}
            autoComplete="birthdate"
            required
          />
          <InputRaces
            label={t('race')}
            placeholder={t('chooseAnOption')}
            value={register?.race?.toString()}
            className="mb-3"
            name="register-race"
            id="register-race"
            onChange={(value) => handleChange('race', value)}
            onValidate={(valid) => validate('race', valid)}
            requiredOnLoad
            disabled={loading}
            required
          />
          <InputGenders
            label={t('genre')}
            placeholder={t('chooseAnOption')}
            value={register?.gender}
            className="mb-3"
            name="register-gender"
            id="register-gender"
            onChange={(value) => handleChange('gender', value)}
            onValidate={(valid) => validate('gender', valid)}
            requiredOnLoad
            disabled={loading}
            required
          />
          <InputPhone
            label={t('phone')}
            value={register?.phone ?? ''}
            id="register-phone"
            className="mb-3 col-24 col-md-13"
            type="tel"
            placeholder={'(XX) XXXXX-XXXX'}
            onChange={(value) => handleChange('phone', value)}
            onValidate={(valid) => validate('phone', valid)}
            requiredOnLoad
            required
            externalIcon={phoneIsValid && <i className="fa-check" style={{ color: 'var(--whatsapp)' }}></i>}
          />
          {!phoneIsValid && register?.phone && user?.profileadditionalinfo?.phone?.replace(/\D/g, '') === register?.phone && (
            <Button
              className="phoneValid mb-4 col-24 col-md-13"
              color="var(--black_light)"
              textColor="var(--white)"
              borderColor="var(--transparent)"
              onClick={() => setOpenOverlayPhoneValidation(true)}
            >
              <i className="fa-solid fa-diamond-exclamation yellow mr-3"></i>
              {t('validPhoneLabel')}
            </Button>
          )}
          <button type="submit" style={{ display: 'none' }}></button>
        </form>
      </span>
      <PhoneValidationComponent
        register={register}
        setRegister={setRegister}
        open={openOverlayPhoneValidation}
        setOpen={setOpenOverlayPhoneValidation}
      />
      {documentInputModalConfirmation()}
      <p className="links">
        <i className="fa-file-lines fa-solid gray" style={{ paddingRight: '0.5rem' }}></i>
        <span dangerouslySetInnerHTML={{ __html: t('messageFooter') }} />
      </p>
    </div>
  )
}
export default withTransaction('DataPage', 'component')(DataPage)
